import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import UnsavedChangesModal from "@components/UnsavedChangesModal/UnsavedChangesModal";
import { UserDefinedFieldForm } from "../UserDefinedFieldForm/UserDefinedFieldForm";
import { useUserDefinedFieldMutations } from "../api/useUserDefinedFieldMutations";
import type { UserDefinedFieldFormValues } from "../types/UserDefinedField";

const FORM_ID = "user-defined-field-form";

const initial: UserDefinedFieldFormValues = {
  label: "",
  name: "",
  variableType: "text",
  context: "project",
  required: false,
  minimum: null,
  maximum: null,
  selectOptions: [],
};

export const New = () => {
  const navigate = useNavigate();
  const { create } = useUserDefinedFieldMutations();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextActionRef = useRef<"back" | "add-another">("back");

  const handleSubmit = async (values: UserDefinedFieldFormValues) => {
    const nextAction = nextActionRef.current;
    nextActionRef.current = "back";
    setSubmitting(true);
    try {
      const result: any = await create(values);
      if (nextAction === "add-another") {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: "Field created." });
      } else {
        const newId = result?.data?.id;
        if (newId) {
          navigate(`../${newId}/edit`, {
            state: {
              toast: { variant: "success", text: "Field created." },
            },
          });
        } else {
          setToast({ variant: "success", text: "Field created." });
        }
      }
    } catch (err) {
      console.error("Failed to create user-defined field", err);
      setToast(saveErrorToast(err, "field"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndAddAnother = () => {
    nextActionRef.current = "add-another";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  const handleCancel = () => {
    if (isDirty) setShowUnsavedModal(true);
    else navigate("..");
  };

  return (
    <>
      <AdminFormPage
        title="Add a Field"
        formId={FORM_ID}
        onCancel={handleCancel}
        onSaveAndAddAnother={handleSaveAndAddAnother}
        isSubmitting={submitting}
      >
        <UserDefinedFieldForm
          key={formKey}
          id={FORM_ID}
          initialValues={initial}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onDirtyChange={setIsDirty}
        />
      </AdminFormPage>
      <UnsavedChangesModal
        show={showUnsavedModal}
        handleCancel={() => setShowUnsavedModal(false)}
        handleDontSave={() => {
          setShowUnsavedModal(false);
          navigate("..");
        }}
        handleSave={() => {
          setShowUnsavedModal(false);
          (
            document.getElementById(FORM_ID) as HTMLFormElement | null
          )?.requestSubmit();
        }}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
